import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import type { ResultsAccessControl } from '../types/form';
import ResultsAccessGate from '../components/results/ResultsAccessGate';

interface FormField {
  id: string;
  name: string;
  label: string;
  type: string;
}

interface Submission {
  id: string;
  formVersion: number;
  data: Record<string, unknown>;
  metadata: {
    submittedAt: string;
    ip?: string;
  };
  status: string;
  createdAt: string;
}

interface ResultsData {
  form: {
    name: string;
    slug: string;
    fields: FormField[];
  };
  submissions: Submission[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  accessControl?: ResultsAccessControl;
}

export default function PublicResultsPage() {
  const { key } = useParams<{ key: string }>();
  const [data, setData] = useState<ResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  // Check access on mount
  useEffect(() => {
    if (!key) return;

    async function checkAccess() {
      try {
        // First, fetch basic info to check if access control is enabled
        const response = await fetch(`/api/results/${key}?page=1&limit=1`);
        if (!response.ok) {
          throw new Error('Invalid results link');
        }
        const result = await response.json() as ResultsData;

        // Check if access control is enabled
        if (result.accessControl?.enabled) {
          // Check if we have a valid access token
          const token = localStorage.getItem(`results_access_${key}`);
          if (token) {
            setAccessGranted(true);
          }
        } else {
          // No access control, proceed
          setAccessGranted(true);
        }

        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load results');
      } finally {
        setCheckingAccess(false);
      }
    }

    checkAccess();
  }, [key]);

  // Load results when access is granted and page changes
  useEffect(() => {
    if (!key || !accessGranted) return;

    async function loadResults() {
      try {
        setLoading(true);
        const token = localStorage.getItem(`results_access_${key}`);
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`/api/results/${key}?page=${page}`, { headers });
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            // Token expired or invalid
            localStorage.removeItem(`results_access_${key}`);
            setAccessGranted(false);
            setCheckingAccess(false);
            return;
          }
          throw new Error('Invalid results link');
        }
        const result = await response.json() as ResultsData;
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load results');
      } finally {
        setLoading(false);
      }
    }

    loadResults();
  }, [key, page, accessGranted]);

  function formatValue(value: unknown, fieldType: string): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    if (fieldType === 'signature' && typeof value === 'string' && value.startsWith('data:image')) {
      return '[Signature]';
    }
    return String(value);
  }

  function renderCellValue(value: unknown, fieldType: string): React.ReactNode {
    if (value === null || value === undefined) return '-';

    // Render signature as an image
    if (fieldType === 'signature' && typeof value === 'string' && value.startsWith('data:image')) {
      return (
        <img
          src={value}
          alt="Signature"
          className="h-10 w-auto max-w-[150px] border border-gray-200 rounded bg-white"
        />
      );
    }

    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  async function exportToExcel() {
    if (!data || !key) return;

    try {
      setExporting(true);

      // Fetch all submissions for export (not just current page)
      const allSubmissions: Submission[] = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(`/api/results/${key}?page=${currentPage}&limit=100`);
        if (!response.ok) throw new Error('Failed to fetch data');
        const pageData = await response.json() as ResultsData;
        allSubmissions.push(...pageData.submissions);
        hasMore = currentPage < pageData.pagination.totalPages;
        currentPage++;
      }

      // Prepare data for Excel
      const excelData = allSubmissions.map((submission, index) => {
        const row: Record<string, string | number> = {
          '#': index + 1,
          'Date': new Date(submission.createdAt).toLocaleString(),
        };

        data.form.fields.forEach((field) => {
          row[field.label] = formatValue(submission.data[field.name], field.type);
        });

        return row;
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Submissions');

      // Auto-size columns
      const colWidths = Object.keys(excelData[0] || {}).map((key) => ({
        wch: Math.max(key.length, 15)
      }));
      worksheet['!cols'] = colWidths;

      // Download
      const filename = `${data.form.name.replace(/[^a-z0-9]/gi, '_')}_submissions_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  // Show access gate if access control is enabled and not granted
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (data?.accessControl?.enabled && !accessGranted) {
    return (
      <ResultsAccessGate
        resultsKey={key || ''}
        formName={data.form.name}
        accessControl={data.accessControl}
        onAccessGranted={() => setAccessGranted(true)}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-500">{error || 'This results link is not valid or has expired.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{data.form.name} - Results</h1>
            <p className="text-gray-500 mt-1">
              {data.pagination.total} submission{data.pagination.total !== 1 ? 's' : ''}
            </p>
          </div>
          {data.submissions.length > 0 && (
            <button
              onClick={exportToExcel}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {exporting ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export to Excel
                </>
              )}
            </button>
          )}
        </div>

        {/* Results Table */}
        {data.submissions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions yet</h3>
            <p className="text-gray-500">Submissions will appear here once the form receives responses.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    {data.form.fields.map((field) => (
                      <th
                        key={field.id}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.submissions.map((submission, index) => (
                    <tr key={submission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(page - 1) * data.pagination.limit + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(submission.createdAt).toLocaleString()}
                      </td>
                      {data.form.fields.map((field) => (
                        <td
                          key={field.id}
                          className={`px-6 py-4 text-sm text-gray-900 ${field.type === 'signature' ? '' : 'whitespace-nowrap max-w-xs truncate'}`}
                          title={field.type === 'signature' ? undefined : formatValue(submission.data[field.name], field.type)}
                        >
                          {renderCellValue(submission.data[field.name], field.type)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {(page - 1) * data.pagination.limit + 1} to{' '}
                  {Math.min(page * data.pagination.limit, data.pagination.total)} of{' '}
                  {data.pagination.total} results
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-gray-400">
            This is a shared results view. Do not share this link publicly.
          </p>
        </div>
      </div>
    </div>
  );
}
